import {
    lazy,
    Suspense,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { CanvasTexture } from 'three';
import type { LabData } from '../AppData/LabData';
import ProductTextureContext from './_contexts/ProductTextureContext';
import ViewerCanvas from './Scene/Canvas';
import ModelViewerTextureUpdater from './ModelViewerTextureUpdater';
import type { CameraView } from './Camera/types';
import {
    resolveModelViewerPreviewData,
    type ModelViewerPreviewDataInput,
} from './previewData';
import type { ModelViewerRenderSupersampling } from './Rendering/supersampling';
import {
    createModelViewerTextureUpdateSignal,
} from './Scene/TextureUpdateSignal';
import type { ModelViewerMode } from './ViewerMode';
import {
    getModelViewerModelIds,
    resolveModelViewerModelId,
} from './ModelVariant';
import useEntryAnimationStartedAt from './Camera/useEntryAnimationStartedAt';

const DragIndicator = lazy(
    async () => await import('./Camera/DragIndicator'),
)
const ModelVariantSelector = lazy(
    async () => await import('./ModelVariantSelector'),
)

interface Props {
    renderTexture: () => HTMLCanvasElement | OffscreenCanvas | HTMLImageElement | undefined
    productId: number,
    labData?: LabData,
    options:Immutable.Map<string,string>
    variant: string,
    cameraCommandId?: number,
    modelRevision?: number,
    cameraRevision?: number,
    cameraEntryStartedAt?: number|null,
    textureRevision?: number,
    cameraPanEnabled?: boolean,
    viewerMode: ModelViewerMode,
    showModelVariantSelector?: boolean,
    previewData?: ModelViewerPreviewDataInput,
    renderSupersampling?: ModelViewerRenderSupersampling,
    liveTextureUpdates?: boolean,
    onCameraChange?: (view: CameraView) => void,
    onTexturesUpdated?: (artwork: CanvasTexture, effects?: CanvasTexture) => void,
}

/**
 * Public facade used by the Design Lab, order dialog, standalone workspace,
 * and static render page.
 *
 * Texture compositing remains outside the R3F root, while ViewerCanvas owns
 * all Three scene state. ProductTextureContext and TextureUpdateSignal are the
 * two bridges between those lifecycles.
 */
export default function ModelViewer(props:Props) {
    const interactive = props.viewerMode !== 'render'
    const websiteMode = props.viewerMode === 'website'
    const [productTexture, setProductTexture] = useState<CanvasTexture|null>(null)
    const [textureError, setTextureError] = useState<Error|null>(null)
    const [requestedModelVariant, setRequestedModelVariant] = useState<string>()
    const [controlsWereUsed, setControlsWereUsed] = useState(false)
    const fxTexture = useRef<CanvasTexture>()
    const textureUpdateSignal = useMemo(
        createModelViewerTextureUpdateSignal,
        [],
    )
    const handleTextureUpdated = useCallback((revision?: number) => {
        textureUpdateSignal.publish(revision)
        if(productTexture !== null) {
            props.onTexturesUpdated?.(
                productTexture,
                props.labData?.useFxCanvas ? fxTexture.current : undefined,
            )
        }
    }, [textureUpdateSignal, productTexture, props.onTexturesUpdated, props.labData?.useFxCanvas])
    const handleControlsUsed = useCallback(() => {
        setControlsWereUsed(true)
    }, [])

    // FX textures are optional secondary atlases used by products such as
    // scarves. A ref avoids rebuilding the provider value on every FX draw.
    const setFxTexture = useCallback((texture:CanvasTexture) => {
        fxTexture.current = texture;
    }, [])

    useEffect(() => {
        setTextureError(null)
    }, [props.productId, props.variant])
    
    const textureContextValue:[CanvasTexture,CanvasTexture|undefined] = useMemo(() => {
        return [productTexture ?? new CanvasTexture(new OffscreenCanvas(1024, 1024)), fxTexture.current];
    }, [productTexture])

    const modelVariants = useMemo(
        () => getModelViewerModelIds(props.labData, props.variant),
        [props.labData, props.variant],
    )
    const selectedModelVariant = (
        requestedModelVariant
        && modelVariants.includes(requestedModelVariant)
    )
        ? requestedModelVariant
        : modelVariants[0]
    const resolvedModel = useMemo(() => {
        if(!props.labData) return selectedModelVariant

        return resolveModelViewerModelId(
            props.labData,
            props.variant,
            resolveModelViewerPreviewData(props.previewData).model,
            selectedModelVariant,
        )
    }, [
        props.labData,
        props.previewData,
        props.variant,
        selectedModelVariant,
    ])

    const localCameraEntryStartedAt = useEntryAnimationStartedAt({
        enabled: websiteMode,
        productId: props.productId,
        labDataId: props.labData?.id,
        variant: props.variant,
        model: resolvedModel,
    })
    const cameraEntryStartedAt = props.cameraEntryStartedAt === null
        ? undefined
        : props.cameraEntryStartedAt === undefined
            ? localCameraEntryStartedAt
            : localCameraEntryStartedAt === undefined
                ? undefined
                : props.cameraEntryStartedAt
    
    if(!props.labData) {
        return null;
    }

    if (textureError) {
        throw textureError
    }

    return <ProductTextureContext.Provider value={textureContextValue}>
        {websiteMode ? <Suspense fallback={null}>
            <DragIndicator controlsWereUsed={controlsWereUsed} />
        </Suspense> : null}
        <ModelViewerTextureUpdater
            productId={props.productId}
            labData={props.labData}
            options={props.options}
            variant={props.variant}
            renderTexture={props.renderTexture}
            setProductTexture={setProductTexture}
            setFxTexture={setFxTexture}
            onError={setTextureError}
            textureRevision={props.textureRevision}
            viewerMode={props.viewerMode}
            liveTextureUpdates={props.liveTextureUpdates}
            onTextureUpdated={handleTextureUpdated}
        />
        <ViewerCanvas
            productId={props.productId}
            labData={props.labData}
            options={props.options}
            variant={props.variant}
            cameraCommandId={props.cameraCommandId}
            modelRevision={props.modelRevision}
            cameraRevision={props.cameraRevision}
            textureUpdateSignal={textureUpdateSignal}
            cameraPanEnabled={props.cameraPanEnabled}
            cameraEntryStartedAt={cameraEntryStartedAt}
            viewerMode={props.viewerMode}
            previewData={props.previewData}
            renderSupersampling={props.renderSupersampling}
            onControlsUsed={websiteMode ? handleControlsUsed : undefined}
            onCameraChange={props.onCameraChange}
            selectedModelVariant={selectedModelVariant}
        />
        {interactive && props.showModelVariantSelector !== false
            ? <Suspense fallback={null}>
                <ModelVariantSelector
                    modelVariants={modelVariants}
                    selectedModelVariant={selectedModelVariant}
                    onSelectModelVariant={setRequestedModelVariant}
                />
            </Suspense>
            : null}
    </ProductTextureContext.Provider>
}
